import {
  canCurrentUserSendGroupInviteForEventThrows,
  groupHasLiveEvents as communityHasLiveEvents,
  eventSidebarHasContentThrows,
  findLiveEventWithoutStatusByBroadcastId as findEvent,
  findLiveEventByBroadcastIdThrows as findEventThrows,
  getEventIdThrows,
  getEventPermissionsForCurrentUserThrows,
  getIsEmbeddableThrows,
  getIsThreadStarterRestrictedThrows,
  isCurrentUserEventAdmin,
  isCurrentUserEventAdminThrows,
  isCurrentUserEventOrganizerThrows,
  isCurrentUserEventPresenterThrows,
  isCurrentUserEventProducerThrows,
  restrictedPostingIsEditableByCurrentUserThrows,
  showAttendeeInfoBar,
  showAttendeeRestrictedErrorBar,
  showEventPublisherToUser,
  showRestrictedPostingWarningBarToUserThrows,
} from '@ms/yammer-data/dist/domains/liveEvents/selectors';
import { getGroupIdThrows } from '@ms/yammer-data/dist/domains/liveEventsStatuses/selectors';
import {
  Group as Community,
  LiveEvent as Event,
  LiveEventWithStatus as EventWithStatus,
} from '@ms/yammer-data/dist/state/types';

import { useSelector } from '../useSelector';

type UseCommunityHasLiveEvents = (communityId: Community['id']) => ReturnType<typeof communityHasLiveEvents>;
export const useCommunityHasLiveEvents: UseCommunityHasLiveEvents = (communityId) =>
  useSelector((state) => communityHasLiveEvents(state, communityId));

type UseIsEventOrganizerThrows = (
  broadcastId: Event['broadcastId']
) => ReturnType<typeof isCurrentUserEventProducerThrows>;
export const useIsEventOrganizerThrows: UseIsEventOrganizerThrows = (broadcastId) =>
  useSelector((state) => isCurrentUserEventOrganizerThrows(state, broadcastId));

type UseIsEventProducerThrows = (
  broadcastId: Event['broadcastId']
) => ReturnType<typeof isCurrentUserEventProducerThrows>;
export const useIsEventProducerThrows: UseIsEventProducerThrows = (broadcastId) =>
  useSelector((state) => isCurrentUserEventProducerThrows(state, broadcastId));

type UseIsEventPresenterThrows = (
  broadcastId: Event['broadcastId']
) => ReturnType<typeof isCurrentUserEventPresenterThrows>;
export const useIsEventPresenterThrows: UseIsEventPresenterThrows = (broadcastId) =>
  useSelector((state) => isCurrentUserEventPresenterThrows(state, broadcastId));

type UseIsCurrentUserEventAdminThrows = (
  broadcastId: Event['broadcastId']
) => ReturnType<typeof isCurrentUserEventAdminThrows>;
export const useIsCurrentUserEventAdminThrows: UseIsCurrentUserEventAdminThrows = (broadcastId) =>
  useSelector((state) => isCurrentUserEventAdminThrows(state, broadcastId));

type UseIsCurrentUserEventAdmin = (
  broadcastId: Event['broadcastId'] | undefined
) => ReturnType<typeof isCurrentUserEventAdmin>;
export const useIsCurrentUserEventAdmin: UseIsCurrentUserEventAdmin = (broadcastId) =>
  useSelector((state) => {
    if (!broadcastId) {
      return false;
    }

    return isCurrentUserEventAdmin(state, broadcastId);
  });

type UseGetEventPermissionsForCurrentUserThrows = (
  broadcastId: Event['broadcastId']
) => ReturnType<typeof getEventPermissionsForCurrentUserThrows>;
export const useGetEventPermissionsForCurrentUserThrows: UseGetEventPermissionsForCurrentUserThrows = (broadcastId) =>
  useSelector((state) => getEventPermissionsForCurrentUserThrows(state, broadcastId));

type UseEventWithoutStatus = (broadcastId: Event['broadcastId'] | undefined) => ReturnType<typeof findEvent>;
export const useEventWithoutStatus: UseEventWithoutStatus = (broadcastId) =>
  useSelector((state) => {
    if (!broadcastId) {
      return undefined;
    }

    return findEvent(state, broadcastId);
  });

type UseEventThrows = (broadcastId: Event['broadcastId']) => ReturnType<typeof findEventThrows>;
export const useEventThrows: UseEventThrows = (broadcastId) =>
  useSelector((state) => findEventThrows(state, broadcastId));

type UseCanCurrentUserSendGroupInviteForEventThrows = (
  broadcastId: Event['broadcastId']
) => ReturnType<typeof canCurrentUserSendGroupInviteForEventThrows>;
export const useCanCurrentUserSendGroupInviteForEventThrows: UseCanCurrentUserSendGroupInviteForEventThrows = (
  broadcastId
) => useSelector((state) => canCurrentUserSendGroupInviteForEventThrows(state, broadcastId));

type UseIsEmbeddableThrows = (broadcastId: Event['broadcastId']) => ReturnType<typeof getIsEmbeddableThrows>;
export const useIsEmbeddableThrows: UseIsEmbeddableThrows = (broadcastId) =>
  useSelector((state) => getIsEmbeddableThrows(state, broadcastId));

type UseStatusThrows = (broadcastId: Event['broadcastId']) => EventWithStatus['status'];
export const useStatusThrows: UseStatusThrows = (broadcastId) =>
  useSelector((state) => findEventThrows(state, broadcastId).status);

type UseProductionEmbedUrlThrows = (broadcastId: Event['broadcastId']) => Event['productionEmbedUrl'];
export const useProductionEmbedUrlThrows: UseProductionEmbedUrlThrows = (broadcastId) =>
  useSelector((state) => findEventThrows(state, broadcastId).productionEmbedUrl);

type UseEditLinkThrows = (broadcastId: Event['broadcastId']) => Event['editLink'];
export const useEditLinkThrows: UseEditLinkThrows = (broadcastId) =>
  useSelector((state) => findEventThrows(state, broadcastId).editLink);

type UseIngestThrows = (broadcastId: Event['broadcastId']) => Event['ingest'];
export const useIngestThrows: UseIngestThrows = (broadcastId) =>
  useSelector((state) => findEventThrows(state, broadcastId).ingest);

type UseEventIdThrows = (broadcastId: Event['broadcastId']) => ReturnType<typeof getEventIdThrows>;
export const useEventIdThrows: UseEventIdThrows = (broadcastId) =>
  useSelector((state) => getEventIdThrows(state, broadcastId));

type UseTitleThrows = (broadcastId: Event['broadcastId']) => Event['title'];
export const useTitleThrows: UseTitleThrows = (broadcastId) =>
  useSelector((state) => findEventThrows(state, broadcastId).title);

type UseStartAtThrows = (broadcastId: Event['broadcastId']) => Event['startAt'];
export const useStartAtThrows: UseStartAtThrows = (broadcastId) =>
  useSelector((state) => findEventThrows(state, broadcastId).startAt);

type UseGroupIdThrows = (broadcastId: Event['broadcastId']) => ReturnType<typeof getGroupIdThrows>;
export const useGroupIdThrows: UseGroupIdThrows = (broadcastId) =>
  useSelector((state) => getGroupIdThrows(state, broadcastId));

type UseEndAtThrows = (broadcastId: Event['broadcastId']) => Event['endAt'];
export const useEndAtThrows: UseEndAtThrows = (broadcastId) =>
  useSelector((state) => findEventThrows(state, broadcastId).endAt);

type UseStreamLinkThrows = (broadcastId: Event['broadcastId']) => Event['streamLink'];
export const useStreamLinkThrows: UseStreamLinkThrows = (broadcastId) =>
  useSelector((state) => findEventThrows(state, broadcastId).streamLink);

type UseDescriptionThrows = (broadcastId: Event['broadcastId']) => Event['description'];
export const useDescriptionThrows: UseDescriptionThrows = (broadcastId) =>
  useSelector((state) => findEventThrows(state, broadcastId).description);

type UseIcsBlobLinkThrows = (broadcastId: Event['broadcastId']) => Event['icsBlobLink'];
export const useIcsBlobLinkThrows: UseIcsBlobLinkThrows = (broadcastId) =>
  useSelector((state) => findEventThrows(state, broadcastId).icsBlobLink);

type UseShowRestrictedPostingWarningBarToUserThrows = (
  broadcastId: Event['broadcastId']
) => ReturnType<typeof showRestrictedPostingWarningBarToUserThrows>;
export const useShowRestrictedPostingWarningBarToUserThrows: UseShowRestrictedPostingWarningBarToUserThrows = (
  broadcastId
) => useSelector((state) => showRestrictedPostingWarningBarToUserThrows(state, broadcastId));

type UseShowAttendeeRestrictedErrorBar = (
  broadcastId: Event['broadcastId']
) => ReturnType<typeof showAttendeeRestrictedErrorBar>;
export const useShowAttendeeRestrictedErrorBar: UseShowAttendeeRestrictedErrorBar = (broadcastId) =>
  useSelector((state) => showAttendeeRestrictedErrorBar(state, broadcastId));

type UseShowEventPublisherToUser = (broadcastId: Event['broadcastId']) => ReturnType<typeof showEventPublisherToUser>;
export const useShowEventPublisherToUser: UseShowEventPublisherToUser = (broadcastId) =>
  useSelector((state) => showEventPublisherToUser(state, broadcastId));

type UseShowAttendeeInfoBar = (broadcastId: Event['broadcastId']) => ReturnType<typeof showAttendeeInfoBar>;
export const useShowAttendeeInfoBar: UseShowAttendeeInfoBar = (broadcastId) =>
  useSelector((state) => showAttendeeInfoBar(state, broadcastId));

type UseIsThreadStarterRestrictedByIdThrows = (
  eventId: Event['id']
) => ReturnType<typeof getIsThreadStarterRestrictedThrows>;
export const useIsThreadStarterRestrictedByIdThrows: UseIsThreadStarterRestrictedByIdThrows = (eventId) =>
  useSelector((state) => getIsThreadStarterRestrictedThrows(state, eventId));

type UseRealtimeChannelIdThrows = (broadcastId: Event['broadcastId']) => Event['realtimeChannelId'];
export const useRealtimeChannelIdThrows: UseRealtimeChannelIdThrows = (broadcastId) =>
  useSelector((state) => findEventThrows(state, broadcastId).realtimeChannelId);

type UseIsGroupCalendarInviteSentThrows = (broadcastId: Event['broadcastId']) => Event['isGroupCalendarInviteSent'];
export const useIsGroupCalendarInviteSentThrows: UseIsGroupCalendarInviteSentThrows = (broadcastId) =>
  useSelector((state) => findEventThrows(state, broadcastId).isGroupCalendarInviteSent);

type UseIsThreadStarterAdminModeratedThrows = (
  broadcastId: Event['broadcastId']
) => Event['isThreadStarterAdminModerated'];
export const useIsThreadStarterAdminModeratedThrows: UseIsThreadStarterAdminModeratedThrows = (broadcastId) =>
  useSelector((state) => findEventThrows(state, broadcastId).isThreadStarterAdminModerated);

type UseOrganizerIdThrows = (broadcastId: Event['broadcastId']) => Event['organizerId'];
export const useOrganizerIdThrows: UseOrganizerIdThrows = (broadcastId) =>
  useSelector((state) => findEventThrows(state, broadcastId).organizerId);

type UseProducerIdsThrows = (broadcastId: Event['broadcastId']) => Event['producerIds'];
export const useProducerIdsThrows: UseProducerIdsThrows = (broadcastId) =>
  useSelector((state) => findEventThrows(state, broadcastId).producerIds);

type UsePresenterIdsThrows = (broadcastId: Event['broadcastId']) => Event['presenterIds'];
export const usePresenterIdsThrows: UsePresenterIdsThrows = (broadcastId) =>
  useSelector((state) => findEventThrows(state, broadcastId).presenterIds);

type UseRestrictedPostingIsEditableByCurrentUserThrows = (
  broadcastId: Event['broadcastId']
) => ReturnType<typeof restrictedPostingIsEditableByCurrentUserThrows>;
export const useRestrictedPostingIsEditableByCurrentUserThrows: UseRestrictedPostingIsEditableByCurrentUserThrows = (
  broadcastId
) => useSelector((state) => restrictedPostingIsEditableByCurrentUserThrows(state, broadcastId));

export type UseEventSidebarHasContentThrows = (
  broadcastId: Event['broadcastId']
) => ReturnType<typeof eventSidebarHasContentThrows>;
export const useEventSidebarHasContentThrows: UseEventSidebarHasContentThrows = (broadcastId) =>
  useSelector((state) => eventSidebarHasContentThrows(state, broadcastId));

type UseTeamsJoinLinkThrows = (broadcastId: Event['broadcastId']) => Event['teamsJoinLink'];
export const useTeamsJoinLinkThrows: UseTeamsJoinLinkThrows = (broadcastId) =>
  useSelector((state) => findEventThrows(state, broadcastId).teamsJoinLink);

type UseRtmpSettingsThrows = (broadcastId: Event['broadcastId']) => Event['rtmpSettings'];
export const useRtmpSettingsThrows: UseRtmpSettingsThrows = (broadcastId) =>
  useSelector((state) => findEventThrows(state, broadcastId).rtmpSettings);
